import type { ListParams } from "../list.type";
import type {
  UpdateFilterValuesProps,
  UseListParamsDebounceProps,
  UseListParamsHandlerProps,
  UseListParamsTableViewProps,
} from "./list-params.type";
import type {
  TableViewFilterKey,
  TableViewParams,
  TableViewSortItem,
} from "@/entities/table-view";

import { useDebounce } from "@react-hook/debounce";
import omit from "lodash/omit";
import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router";

import {
  useCreateUserTableView,
  useGetUserTableView,
} from "@/entities/table-view";

import {
  buildSearchParams,
  searchContainsAtLeastOneListParams,
  getListParamsFromSearch,
  checkIfListIsActive,
  paramsAreEquals,
} from "./list-params.helper";

export const useListParamsHandler = ({
  storageKey,
  contextTabKey,
  contextTabValue,
  defaultSort = [],
  defaultPageSize = 10,
}: UseListParamsHandlerProps) => {
  const { state: locationState } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const isListActive = checkIfListIsActive({
    contextTabKey,
    contextTabValue,
    locationState,
    searchParams,
  });

  const hasUrlListParams = searchContainsAtLeastOneListParams({
    searchParams,
    storageKey,
  });

  const {
    params: dbParams,
    saveParams,
    isLoading,
  } = useListParamsTableView({
    tableKey: storageKey,
    enabled: isListActive,
  });

  const { sort, filters, pagination } = getListParamsFromSearch({
    searchParams,
    storageKey,
    defaultSort: dbParams?.sort ?? defaultSort,
    defaultFilters: dbParams?.filters ?? {},
    defaultPageSize: dbParams?.pageSize ?? defaultPageSize,
  });

  useEffect(() => {
    if (!isListActive || isLoading) return;

    if (hasUrlListParams) {
      const urlParams = getListParamsFromSearch({
        searchParams,
        storageKey,
        defaultSort: [],
        defaultPageSize,
      });
      saveParams({
        sort: urlParams.sort,
        filters: urlParams.filters,
        pageSize: urlParams.pagination.pageSize,
      });
    } else {
      const restoredSearch = buildSearchParams({
        sort: dbParams?.sort ?? defaultSort,
        filters: dbParams?.filters ?? {},
        pageSize: dbParams?.pageSize ?? defaultPageSize,
        searchParams,
        storageKey,
      });
      // Always keep the current location state when updating history with list params.
      // This is particularly useful for keeping the state of pages opened in modals.
      setSearchParams(restoredSearch, { replace: true, state: locationState });
    }
  }, [searchParams, dbParams, isLoading]);

  const updateFilterValues = ({
    filterKey,
    filterValues,
  }: UpdateFilterValuesProps) => {
    const updatedFilters = { ...filters, [filterKey]: filterValues };
    updateSearchParams({ sort, filters: updatedFilters, pagination });
  };

  const clearFilter = (filterKey: TableViewFilterKey) => {
    const updatedFilters = omit(filters, filterKey);
    updateSearchParams({ sort, filters: updatedFilters, pagination });
  };

  const clearFilters = () => {
    updateSearchParams({ sort, filters: {}, pagination });
  };

  const clearSort = (sortKey: TableViewSortItem) => {
    const updatedSort = sort.filter((s) => s !== sortKey);
    updateSearchParams({ sort: updatedSort, filters, pagination });
  };

  const clearFiltersAndSorts = () => {
    updateSearchParams({ sort: [], filters: {}, pagination });
  };

  const resetFiltersAndSorts = () => {
    updateSearchParams({ sort: defaultSort, filters: {}, pagination });
  };

  const updateSearchParams = ({ sort, filters, pagination }: ListParams) => {
    const newSearch = buildSearchParams({
      sort,
      filters,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      searchParams,
      storageKey,
    });

    // Always keep the current location state when updating history with list params.
    // This is particularly useful for keeping the state of pages opened in modals.
    setSearchParams(newSearch, { state: locationState });
  };

  return {
    sort,
    filters,
    pagination,
    clearFilter,
    clearFilters,
    clearSort,
    clearFiltersAndSorts,
    resetFiltersAndSorts,
    updateFilterValues,
    updateSearchParams,
  };
};

// React Query Optimistic Update
// Here we use "Via the UI" way, see documentation:
// https://tanstack.com/query/latest/docs/framework/react/guides/optimistic-updates#via-the-ui

export const useListParamsTableView = ({
  tableKey,
  enabled,
}: UseListParamsTableViewProps) => {
  const { tableView, isPending: isLoading } = useGetUserTableView(tableKey, {
    enabled,
    neverStale: true,
    persist: true, // persist data in local storage
  });
  const { mutate: create, ...mutation } = useCreateUserTableView(tableKey);

  const savedParams = tableView?.parameters;
  const optimisticParams = mutation.variables?.parameters;

  // When mutation is processing return optimistic data otherwise return
  // real data from database because at this point db is up to date.
  const params = mutation.isPending ? optimisticParams : savedParams;

  const shouldSaveParams = (newParams: TableViewParams) => {
    if (!enabled || isLoading) return false;
    if (paramsAreEquals(params, newParams)) return false;
    return true;
  };

  const saveParamsInDb = (newParams: TableViewParams) => {
    if (!shouldSaveParams(newParams)) return;

    // We always POST params (instead of PATCH) even if the table view already
    // has data in db. It's the api responsibility to update instead of create
    // if data already exist. This behavior only works with `is_default=true`.
    create({ parameters: newParams, is_default: true });
  };

  const { setDebounceParams } = useListParamsDebounce({
    onParamsChanged: saveParamsInDb,
  });

  return {
    params,
    saveParams: setDebounceParams,
    isLoading,
  };
};

export const useListParamsDebounce = ({
  onParamsChanged,
}: UseListParamsDebounceProps) => {
  const [params, setParams] = useDebounce<TableViewParams | null>(null, 500);

  useEffect(() => {
    if (params === null) return;
    onParamsChanged?.(params);
  }, [params]);

  const setDebounceParams = (newParams: TableViewParams) => {
    if (params === null) setParams(newParams);
    else setParams({ ...params, ...newParams });
  };

  return {
    setDebounceParams,
  };
};
